import { MobileNavBar, Modal, PixelIcon } from '@/components/ui';
import { getWorldForLevel } from '@/config/worlds';
import { useEffects, useGame, useSound } from '@/contexts';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { ThemeManager } from '@/services/themeManager';
import { getMenuHapticPattern } from '@/utils/haptics';
import type { SettingsMenuProps } from '@/types/game';
import React from 'react';
import { useLocation } from 'react-router-dom';

function SettingsMenu({
  isOpen,
  onClose,
  onRestart,
  onGoHome,
  onToggleFullscreen,
}: SettingsMenuProps) {
  const { gameState, updateGameState } = useGame();
  const { playButtonClick, setVolume, setEnabled, getVolume, setMusicEnabled, isMusicEnabled, setMusicVolume, getMusicVolume, startWorldMusic, startLobbyMusic } = useSound();
  const { setEffectsEnabled } = useEffects();
  const { pattern } = useHapticFeedback();
  const location = useLocation();
  const [activeTab, setActiveTab] = React.useState<string>('audio');
  const [hasTriggeredOpenFeedback, setHasTriggeredOpenFeedback] = React.useState(false);
  const [volumePercent, setVolumePercent] = React.useState<number>(() =>
    Math.round(getVolume() * 100)
  );
  const [musicOn, setMusicOn] = React.useState<boolean>(() => isMusicEnabled());
  const [musicVolumePercent, setMusicVolumePercent] = React.useState<number>(() =>
    Math.round(getMusicVolume() * 100)
  );

  // Check if we're on the game route
  const isOnGameRoute = location.pathname === '/game';
  const currentWorld = getWorldForLevel(gameState.level ?? 0);

  React.useEffect(() => {
    if (!isOpen) {
      setHasTriggeredOpenFeedback(false);
      return;
    }
    if (!isOnGameRoute || hasTriggeredOpenFeedback) return;

    pattern(getMenuHapticPattern('menuOpen'));
    setHasTriggeredOpenFeedback(true);
  }, [hasTriggeredOpenFeedback, isOnGameRoute, isOpen, pattern]);

  React.useEffect(() => {
    if (!isOpen) return;
    setActiveTab(isOnGameRoute ? 'game' : 'audio');
    setVolumePercent(Math.round(getVolume() * 100));
    setMusicOn(isMusicEnabled());
    setMusicVolumePercent(Math.round(getMusicVolume() * 100));
  }, [isOpen, isOnGameRoute]);

  const handleSettingsChange = (setting: string, value: boolean | string | number) => {
    updateGameState({
      gameSettings: {
        ...gameState.gameSettings,
        [setting]: value,
      },
    });

    // Apply setting changes immediately
    if (setting === 'sound') {
      setEnabled(value as boolean);
    } else if (setting === 'effects') {
      setEffectsEnabled(value as boolean);
    } else if (setting === 'theme') {
      ThemeManager.applyTheme(value as string);
    } else if (setting === 'graphics' || setting === 'vibration') {
      // These are handled by PerformanceOptimizer and device features
      localStorage.setItem(setting, value.toString());
    }
  };

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const volume = parseInt(event.target.value);
    setVolumePercent(volume);
    setVolume(volume / 100);
  };

  const handleMusicVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const vol = parseInt(event.target.value);
    setMusicVolumePercent(vol);
    setMusicVolume(vol / 100);
  };

  const handleButtonClick = (action: () => void) => {
    playButtonClick();
    action();
  };

  // Mobile navigation actions - only show on game route
  const mobileActions = isOnGameRoute
    ? [
        ...(onRestart
          ? [
              {
                id: 'restart',
                label: 'Restart',
                icon: <PixelIcon name="restart" size={24} />,
                onClick: () => handleButtonClick(onRestart),
                variant: 'warning' as const,
                shortcut: 'R',
              },
            ]
          : []),
        ...(onToggleFullscreen
          ? [
              {
                id: 'fullscreen',
                label: 'Fullscreen',
                icon: <PixelIcon name="fullscreen" size={24} />,
                onClick: () => handleButtonClick(onToggleFullscreen),
                variant: 'info' as const,
                shortcut: 'F',
              },
            ]
          : []),
        ...(onGoHome
          ? [
              {
                id: 'home',
                label: 'Home',
                icon: <PixelIcon name="home" size={24} />,
                onClick: () => handleButtonClick(onGoHome),
                variant: 'success' as const,
                shortcut: 'H',
              },
            ]
          : []),
      ]
    : [];

  const tabs = [
    ...(isOnGameRoute ? [{ id: 'game', label: 'Game', icon: 'gamepad' }] : []),
    { id: 'audio', label: 'Audio', icon: 'sound' },
    { id: 'graphics', label: 'Display', icon: 'settings' },
  ];

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Game Settings"
        subtitle={isOnGameRoute ? 'Paused • Configure your game experience' : 'Configure your game experience'}
        variant="info"
        size="medium"
        className={`settings-modal settings-modal--${isOnGameRoute ? currentWorld.id : 'default'}`}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        showCloseButton={true}
        layoutId="nav-button-settings"
        sourceButtonId="settings"
      >
        {isOnGameRoute && (
          <div className="settings-resume-strip menu-pop">
            <button className="btn-primary" onClick={() => handleButtonClick(onClose)}>
              <PixelIcon name="play" className="btn-icon" />
              <span className="btn-text">Resume Game</span>
              <span className="btn-shortcut">ESC</span>
            </button>
          </div>
        )}

        {/* Audio Settings Tab */}
        {activeTab === 'audio' && (
          <div className="settings-section">
            <h4 className="settings-section-title">Audio & Effects</h4>

            <div className="settings-item">
              <label htmlFor="sound-toggle">Sound Effects</label>
              <button
                id="sound-toggle"
                className={`btn-secondary btn-small ${
                  gameState.gameSettings.sound ? 'active' : 'off'
                }`}
                onClick={() => {
                  const newValue = !gameState.gameSettings.sound;
                  handleSettingsChange('sound', newValue);
                  playButtonClick();
                }}
              >
                <PixelIcon name="sound" className="btn-icon" />
                <span className="btn-text toggle-state">
                  {gameState.gameSettings.sound ? 'ON' : 'OFF'}
                </span>
              </button>
            </div>

            <div className="settings-item">
              <label htmlFor="vibration-toggle">Haptic Feedback</label>
              <button
                id="vibration-toggle"
                className={`btn-secondary btn-small ${
                  gameState.gameSettings.vibration ? 'active' : 'off'
                }`}
                onClick={() => {
                  const newValue = !gameState.gameSettings.vibration;
                  handleSettingsChange('vibration', newValue);
                  playButtonClick();
                }}
              >
                <PixelIcon name="vibration" className="btn-icon" />
                <span className="btn-text toggle-state">
                  {gameState.gameSettings.vibration ? 'ON' : 'OFF'}
                </span>
              </button>
            </div>

            <div className="settings-item">
              <label htmlFor="volume-slider">Master Volume</label>
              <div className="volume-control">
                <input
                  type="range"
                  id="volume-slider"
                  className="settings-slider"
                  min="0"
                  max="100"
                  value={volumePercent}
                  onChange={handleVolumeChange}
                />
                <span id="volume-display" className="slider-value">
                  {volumePercent}%
                </span>
              </div>
            </div>

            <h4 className="settings-section-title" style={{ marginTop: 16 }}>Music</h4>

            <div className="settings-item">
              <label htmlFor="music-toggle">Background Music</label>
              <button
                id="music-toggle"
                className={`btn-secondary btn-small ${musicOn ? 'active' : 'off'}`}
                onClick={() => {
                  const newValue = !musicOn;
                  setMusicOn(newValue);
                  setMusicEnabled(newValue);
                  // When toggling music back on, start the appropriate theme
                  if (newValue) {
                    if (isOnGameRoute) {
                      startWorldMusic(currentWorld.id);
                    } else {
                      startLobbyMusic();
                    }
                  }
                  playButtonClick();
                }}
              >
                <PixelIcon name="music" className="btn-icon" />
                <span className="btn-text toggle-state">
                  {musicOn ? 'ON' : 'OFF'}
                </span>
              </button>
            </div>

            <div className="settings-item">
              <label htmlFor="music-volume-slider">Music Volume</label>
              <div className="volume-control">
                <input
                  type="range"
                  id="music-volume-slider"
                  className="settings-slider"
                  min="0"
                  max="100"
                  value={musicVolumePercent}
                  onChange={handleMusicVolumeChange}
                  disabled={!musicOn}
                />
                <span className="slider-value">
                  {musicVolumePercent}%
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Graphics Settings Tab */}
        {activeTab === 'graphics' && (
          <div className="settings-section">
            <h4 className="settings-section-title">Graphics & Theme</h4>

            <div className="settings-item">
              <label htmlFor="graphics-select">Graphics Quality</label>
              <select
                id="graphics-select"
                className="settings-select"
                value={gameState.gameSettings.graphics}
                onChange={e => handleSettingsChange('graphics', e.target.value)}
              >
                <option value="high">High Quality</option>
                <option value="medium">Medium Quality</option>
                <option value="low">Low Quality</option>
              </select>
            </div>

            <div className="settings-item">
              <label htmlFor="effects-toggle">Visual Effects</label>
              <button
                className={`btn-secondary btn-small ${
                  gameState.gameSettings.effects ? 'active' : 'off'
                }`}
                onClick={() => {
                  const newValue = !gameState.gameSettings.effects;
                  handleSettingsChange('effects', newValue);
                  playButtonClick();
                }}
              >
                <PixelIcon name="effects" className="btn-icon" />
                <span className="btn-text toggle-state">
                  {gameState.gameSettings.effects ? 'ON' : 'OFF'}
                </span>
              </button>
            </div>

            <div className="settings-item">
              <label htmlFor="theme-select">Theme</label>
              <select
                id="theme-select"
                className="settings-select"
                value={gameState.gameSettings.theme}
                onChange={e => handleSettingsChange('theme', e.target.value)}
              >
                <option value="default">Default</option>
                <option value="dark">Dark Mode</option>
                <option value="light">Light Mode</option>
                <option value="retro">Retro Pixel</option>
              </select>
            </div>
          </div>
        )}

        {/* Game Actions Tab - Only show on /game route */}
        {activeTab === 'game' && isOnGameRoute && (
          <div className="settings-section">
            <h4 className="settings-section-title">Game Actions</h4>
            <div className="settings-actions">
              {onRestart && (
                <button className="btn-warning" onClick={() => handleButtonClick(onRestart)}>
                  <PixelIcon name="restart" className="btn-icon" />
                  <span className="btn-text">Restart Level</span>
                  <span className="btn-shortcut">R</span>
                </button>
              )}

              {onToggleFullscreen && (
                <button className="btn-info" onClick={() => handleButtonClick(onToggleFullscreen)}>
                  <PixelIcon name="fullscreen" className="btn-icon" />
                  <span className="btn-text">Toggle Fullscreen</span>
                  <span className="btn-shortcut">F</span>
                </button>
              )}

              {onGoHome && (
                <button className="btn-success" onClick={() => handleButtonClick(onGoHome)}>
                  <PixelIcon name="home" className="btn-icon" />
                  <span className="btn-text">Go to Home</span>
                  <span className="btn-shortcut">H</span>
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Mobile Navigation Bar */}
      <MobileNavBar actions={mobileActions} />
    </>
  );
}

export default SettingsMenu;
